<template>
  <div class="category-group-page">
    <a-card title="类目管理" :bordered="false">
      <template slot="extra">
        <a-button type="primary" icon="plus" @click="handleCreate">新增类目</a-button>
      </template>
      <a-table row-key="id" :loading="loading" :columns="columns" :data-source="groups" :pagination="false">
        <span slot="categories" slot-scope="text, record">
          {{ categoryNames(record) }}
        </span>
        <span slot="status" slot-scope="text">
          <a-tag :color="text === 1 ? 'green' : 'orange'">{{ text === 1 ? '启用' : '停用' }}</a-tag>
        </span>
        <span slot="action" slot-scope="text, record">
          <a @click="handleEdit(record)">编辑</a>
          <a-divider type="vertical" />
          <a-popconfirm title="确定删除该类目吗？删除后产品大项将恢复独立展示。" @confirm="handleDelete(record)">
            <a class="danger">删除</a>
          </a-popconfirm>
        </span>
      </a-table>
    </a-card>

    <a-modal v-model="visible" :title="form.id ? '编辑类目' : '新增类目'" width="720px" @ok="handleOk">
      <a-form-model :model="form" :label-col="{ span: 4 }" :wrapper-col="{ span: 19 }">
        <a-form-model-item label="产品大项">
          <div class="category-options">
            <a-checkbox
              v-for="item in categories"
              :key="item.id"
              :checked="isSelected(item.id)"
              :disabled="isOccupied(item.id) && !isSelected(item.id)"
              @change="toggleCategory(item.id, $event.target.checked)"
            >
              {{ item.name }}<span v-if="isOccupied(item.id) && !isSelected(item.id)" class="occupied">（已组合）</span>
            </a-checkbox>
          </div>
        </a-form-model-item>
        <a-form-model-item label="展示顺序">
          <div v-if="selectedCategories.length" class="selected-list">
            <div v-for="(item, index) in selectedCategories" :key="item.id" class="selected-item">
              <span>{{ index + 1 }}. {{ item.name }}</span>
              <span>
                <a-button size="small" icon="arrow-up" :disabled="index === 0" @click="moveCategory(index, -1)" />
                <a-button size="small" icon="arrow-down" :disabled="index === selectedCategories.length - 1" @click="moveCategory(index, 1)" />
                <a-button size="small" type="danger" ghost @click="toggleCategory(item.id, false)">移除</a-button>
              </span>
            </div>
          </div>
          <a-empty v-else description="请至少选择两个产品大项" />
        </a-form-model-item>
        <a-form-model-item label="门户名称">
          <a-input :value="selectedCategories.map(item => item.name).join('/')" disabled />
        </a-form-model-item>
        <a-form-model-item label="类目排序">
          <a-input-number v-model="form.sortOrder" :min="0" style="width:100%;" />
        </a-form-model-item>
        <a-form-model-item label="状态">
          <a-switch :checked="form.status === 1" @change="value => form.status = value ? 1 : 0" />
        </a-form-model-item>
      </a-form-model>
    </a-modal>
  </div>
</template>

<script>
import {
  deleteProductCategoryGroup,
  getProductCategoryGroupList,
  getProductCategoryTree,
  saveProductCategoryGroup
} from '@/api'

export default {
  name: 'ProductCategoryGroup',
  data() {
    return {
      loading: false,
      visible: false,
      groups: [],
      categories: [],
      form: this.emptyForm(),
      columns: [
        { title: '类目名称', dataIndex: 'name' },
        { title: '组合产品大项', key: 'categories', scopedSlots: { customRender: 'categories' } },
        { title: '排序', dataIndex: 'sortOrder', width: 100 },
        { title: '状态', dataIndex: 'status', width: 100, scopedSlots: { customRender: 'status' } },
        { title: '操作', key: 'action', width: 150, scopedSlots: { customRender: 'action' } }
      ]
    }
  },
  computed: {
    selectedCategories() {
      const categoryMap = new Map(this.categories.map(item => [String(item.id), item]))
      return (this.form.categoryIds || []).map(id => categoryMap.get(String(id))).filter(Boolean)
    },
    occupiedCategoryIds() {
      const ids = []
      this.groups.forEach(group => {
        if (group.id === this.form.id) return
        const groupCategoryIds = group.categoryIds || []
        groupCategoryIds.forEach(id => ids.push(String(id)))
      })
      return new Set(ids)
    }
  },
  created() {
    this.loadData()
  },
  methods: {
    emptyForm() {
      return { id: null, categoryIds: [], sortOrder: 0, status: 1 }
    },
    loadData() {
      this.loading = true
      return Promise.all([getProductCategoryGroupList(), getProductCategoryTree()]).then(([groupRes, categoryRes]) => {
        this.groups = groupRes.data || []
        this.categories = categoryRes.data || []
      }).finally(() => { this.loading = false })
    },
    categoryNames(record) {
      return (record.categories || []).map(item => item.name).join(' / ') || '-'
    },
    isSelected(id) {
      return (this.form.categoryIds || []).some(item => String(item) === String(id))
    },
    isOccupied(id) {
      return this.occupiedCategoryIds.has(String(id))
    },
    toggleCategory(id, checked) {
      const ids = (this.form.categoryIds || []).slice()
      const index = ids.findIndex(item => String(item) === String(id))
      if (checked && index < 0) ids.push(id)
      if (!checked && index >= 0) ids.splice(index, 1)
      this.form = { ...this.form, categoryIds: ids }
    },
    moveCategory(index, offset) {
      const target = index + offset
      if (target < 0 || target >= this.form.categoryIds.length) return
      const ids = this.form.categoryIds.slice()
      const current = ids[index]
      this.$set(ids, index, ids[target])
      this.$set(ids, target, current)
      this.form = { ...this.form, categoryIds: ids }
    },
    handleCreate() {
      this.form = this.emptyForm()
      this.visible = true
    },
    handleEdit(record) {
      this.form = {
        id: record.id,
        categoryIds: (record.categoryIds || []).slice(),
        sortOrder: record.sortOrder == null ? 0 : record.sortOrder,
        status: record.status == null ? 1 : record.status
      }
      this.visible = true
    },
    handleOk() {
      if ((this.form.categoryIds || []).length < 2) {
        this.$message.warning('请至少选择两个产品大项')
        return
      }
      saveProductCategoryGroup(this.form).then(() => {
        this.$message.success('保存成功')
        this.visible = false
        this.loadData()
      })
    },
    handleDelete(record) {
      deleteProductCategoryGroup({ id: record.id }).then(() => {
        this.$message.success('删除成功')
        this.loadData()
      })
    }
  }
}
</script>

<style scoped>
.category-group-page { padding: 20px; background: #f0f2f5; min-height: calc(100vh - 96px); }
.category-options { display: grid; grid-template-columns: repeat(3, minmax(0, 1fr)); gap: 10px 16px; }
.category-options .ant-checkbox-wrapper { margin-left: 0; }
.occupied { color: #999; }
.selected-list { border: 1px solid #e8e8e8; border-radius: 4px; }
.selected-item { display: flex; align-items: center; justify-content: space-between; padding: 9px 12px; border-bottom: 1px solid #f0f0f0; }
.selected-item:last-child { border-bottom: 0; }
.selected-item .ant-btn { margin-left: 6px; }
.danger { color: #f5222d; }
</style>
